import { itemCost, type MaterialItem } from "@/store/materialsStore";

export interface CostGroup {
  label: string;
  amount: number;
  share: number;
}

export interface RiskFlag {
  id: string;
  text: string;
  detail: string;
}

export interface CostAnalytics {
  groups: CostGroup[];
  total: number;
  confidencePct: number;
  riskFlags: RiskFlag[];
}

const STRUCTURAL = new Set<MaterialItem["category"]>(["Structure", "Bulk"]);

// Plain-language copy only — this is the reference bar for wording everywhere else.
export function deriveCostAnalytics(items: MaterialItem[]): CostAnalytics {
  const structural = items.filter((m) => STRUCTURAL.has(m.category)).reduce((a, m) => a + itemCost(m), 0);
  const finishes = items.filter((m) => !STRUCTURAL.has(m.category)).reduce((a, m) => a + itemCost(m), 0);
  const total = structural + finishes;

  const steel = items.find((m) => m.id === "rebar");
  const brick = items.find((m) => m.id === "brick");
  const riskFlags: RiskFlag[] = [];
  if (steel && steel.quantity > 0) {
    riskFlags.push({
      id: "steel-price",
      text: "Steel may cost more than planned.",
      detail: "Prices have been moving up recently. Getting a fixed quote now could save you money.",
    });
  }
  if (brick && brick.quantity > 40000) {
    riskFlags.push({
      id: "brick-volume",
      text: "You're ordering a lot of bricks.",
      detail: "Large orders sometimes arrive late. Ask your supplier to confirm the delivery date.",
    });
  }
  if (finishes / Math.max(total, 1) < 0.25) {
    riskFlags.push({
      id: "finishes-low",
      text: "Finishing costs look low for a home this size.",
      detail: "Items like tiles, paint and fittings are often added later. Expect this number to grow.",
    });
  }

  const confidencePct = Math.max(55, Math.min(96, 94 - riskFlags.length * 5));

  return {
    groups: [
      { label: "Structural", amount: structural, share: total ? structural / total : 0 },
      { label: "Finishes", amount: finishes, share: total ? finishes / total : 0 },
    ],
    total,
    confidencePct,
    riskFlags,
  };
}
